import {
  afterNextRender,
  Component,
  computed,
  ContentChild,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  Injector,
  input,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  signal,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import { InitializedEvent } from "devextreme/ui/select_box";
import { nextRuntimeId } from "src/app/support/runtime-id";
import dxTextBox from "devextreme/ui/text_box";
import { Bind } from "src/app/support/decorators/bind";
import DataSource from "devextreme/data/data_source";
import { isArray, isFunction, isString, some } from "lodash-es";
import { Lambda } from "src/app/support/lambda";
import { sntTranslate } from "src/app/support/translate";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import {
  CodiceFiscaleHelperData,
  CodiceFiscaleResult,
  CodiceFiscaleService
} from "src/app/services/codice-fiscale.service";
import { EditableSelectBoxItemTemplateDirective } from "src/app/components/ui/editable/editable-select-box-item-template.directive";
import { DxFormComponent } from "devextreme-angular";
import {
  ValidationRules,
  ValidationService
} from "src/app/services/validation.service";
import { formatAddress } from "src/app/common/format/address-format";
import { IndirizzoServiceSearchResult } from "src/app/services/indirizzo.service";
import { CustomerBackend } from "src/app/backend/api/customer.backend";
import { SsnNotUniqError } from "src/app/errors/ssn-not-uniq.error";
import { ButtonClickAsyncEvent } from "src/app/components/ui/button/button-click-async-event";
import { Address } from "src/app/backend/models/address";
import { VatNumberNotUniqError } from "src/app/errors/vat-number-not-uniq.error";
import {
  getVatNumberCountriesWithoutItaly,
  validateEUVatNumber,
  validateGlobalVatNumber
} from "src/app/support/vat-number";
import { TooltipDirective } from "src/app/modules/bootstrap/directives/tooltip.directive";
import { WysiwygEditorComponent } from "src/app/components/ui/wysiwyg-editor/wysiwyg-editor.component";
import {
  buildPhoneNumberWithPrefix,
  getDefaultPrefixPhone,
  getPhoneNumber,
  getRegionCodePhoneNumber,
  validatePhone
} from "src/app/support/phone";
import ArrayStore from "devextreme/data/array_store";
import { getPrefixNumber } from "src/app/support/customer";
import { hasText } from "src/app/support/text";
import { BootstrapService } from "src/app/modules/bootstrap/bootstrap.service";
import { PLATFORM_TOKEN } from "src/app/tokens/platform.token";
import { PlatformType } from "src/app/common/platform-type";
import { AddressCheckerFunctionService } from "src/app/modules/bootstrap/pms-ui/functions/address/address-checker/address-checker-function.service";
import { ItemClickEvent } from "devextreme/ui/select_box";
import type { ValueChangedEvent } from "devextreme/ui/tag_box";
import { TagLight } from "src/app/backend/models/tag/tag";
import { colorGetLighter, COLORS } from "src/app/support/colors";
import { el } from "src/app/support/dom";
import { compact, trim } from "lodash";
import {
  autoPlacement,
  autoUpdate,
  computePosition,
  offset,
  shift,
  type VirtualElement
} from "@floating-ui/dom";
import { lastValueFrom } from "rxjs";

export type EditableDataType =
  | "text"
  | "textarea"
  | "number"
  | "percent"
  | "date"
  | "time"
  | "datetime"
  | "select"
  | "boolean"
  | "currency"
  | "fiscalCode"
  | "vatCode"
  | "address"
  | "lookup"
  | "custom"
  | "phoneNumber"
  | "html"
  | "tag";

export type EditableCompanionSelectConfig = {
  dataField: string;
  label: { text: string; location: "top" | "bottom"; showColon: boolean };
  dataSource: DataSource | Array<unknown>;
  valueExpr?: string;
  displayExpr?: string | ((item: unknown) => string);
  validationRules?: ValidationRules;
  colSpan?: number;
  initialValue?: unknown;
  searchEnabled?: boolean;
};

export type EditableEditRecapContext = {
  data: unknown;
  formData: Record<string, unknown> | undefined;
};

export type EditableEditRecapLine = {
  label: string;
  value: string;
};

export type EditableSaveConfirmContext = {
  value: unknown;
  oldValue: unknown;
  data: unknown;
  formData: Record<string, unknown> | undefined;
};

export type EditableSaveConfirmConfig = {
  title: string;
  text: string;
  /** When true, No keeps the editor open (fiscal-code). Default false. */
  cancelBlocksSave?: boolean;
};

export type EditableChangedEvent<T = any, K = any> = {
  lock: Lambda<void>;
  unlock: Lambda<void>;
  updated: Lambda<void>;
  value: K;
  oldValue: K;
  data: T;
  executeAsync: (fnAsync: () => Promise<void>) => Promise<void>;
  recalculatePercentages?: boolean;
  companionValues?: Record<string, unknown>;
  saveConfirmAccepted?: boolean;
};

const VAT_NUMBER_CODE_AND_COUNTRIES = getVatNumberCountriesWithoutItaly();
const DEFAULT_CONFIRM_ON_SAVE_TIMEOUT_MS = 5000;

@Component({
  selector: "snt-editable",
  templateUrl: "editable.component.html",
  styleUrls: ["editable.component.scss"],
  standalone: false
})
export class EditableComponent implements OnInit, OnChanges, OnDestroy {
  @ContentChild("placeholderHtmlDataType")
  contentPlaceholderHtml: ElementRef | undefined;

  @ViewChild(DxFormComponent)
  dxForm: DxFormComponent | undefined;

  @ViewChild("editPopupOverlay")
  editPopupOverlay: ElementRef<HTMLElement> | undefined;

  @ViewChild("editPopupPanel")
  editPopupPanel: ElementRef<HTMLElement> | undefined;

  private editPopupReference: HTMLElement | VirtualElement | undefined;
  private editPopupAutoUpdateCleanup?: () => void;

  @Input()
  value: any | undefined;
  valueDisplay: any | undefined;

  @Input()
  editInitialValue: any | undefined;

  @Input()
  data: any | undefined;

  @Input()
  enabled: boolean = true;

  @Input()
  noPaddingLeft: boolean = false;

  @Input()
  noBorder: boolean = false;

  @Input()
  showIcon: boolean = true;

  @Input()
  iconOnTheLeft: boolean = false;

  @Input()
  placeholder?: string;

  @Input()
  fontBold: boolean = false;

  @Input()
  textLight: boolean = false;

  @Input()
  editInline: boolean = false;

  @Input()
  fontSize?: "text-3xl" | "text-xs";

  @Input()
  clickTargetEdit: "icon" | "component" = "icon";

  @Input()
  sup: boolean | undefined;

  @Input()
  dataType: EditableDataType = "text";

  @Input()
  mailto: boolean = false;

  @Input()
  dataSource?: DataSource | Array<any>;

  @Input()
  displayModeDisplayExprData?: () => any;

  @Input()
  displayModeDisplayExprFn?: (data: any) => string;

  @Input()
  mask: string | undefined;

  @Input()
  min: any | undefined;

  @Input()
  max: any | undefined;

  @Input()
  displayExpr?: string | ((value: any) => string);

  @Input()
  tagValueExpr?: string;

  @Input()
  tagDataSourceValueExpr?: string;

  @Input()
  displayOnlyExpr?: string;

  @Input()
  displayPlaceholder?: string;

  @Input()
  fiscalCodeHelperData?: CodiceFiscaleHelperData;

  @Input()
  fiscalCodeCheckConsistencyFn: ((ssn: string) => boolean) | undefined;

  @Input()
  fiscalCodeCheckUniqueness: boolean = false;

  @Input()
  vatNumberCheckUniqueness: boolean = false;

  @Input()
  vatNumberForeign: boolean = false;

  @Input()
  skipAddressChecker: boolean = false;

  @Input()
  fiscalCodeOptional: boolean = false;

  @Input()
  editingWidth: number | undefined;

  @Input()
  validationGroup: string | undefined;

  @Input()
  infoText: string | undefined;

  @Input()
  helpText: string | undefined;

  @Input()
  editRecapBuilder?: (ctx: EditableEditRecapContext) => EditableEditRecapLine[];

  @Input()
  confirmOnSave: boolean = false;

  @Input()
  confirmOnSaveTimeoutMs: number = DEFAULT_CONFIRM_ON_SAVE_TIMEOUT_MS;

  @Input()
  saveConfirmFn?: (
    ctx: EditableSaveConfirmContext
  ) =>
    | EditableSaveConfirmConfig
    | null
    | Promise<EditableSaveConfirmConfig | null>;

  saveConfirmPending: boolean = false;

  saveConfirmMessageConfig: EditableSaveConfirmConfig | null = null;

  saveConfirmMessagePending: boolean = false;

  private pendingSaveConfirmAccepted: boolean | undefined;

  get confirmOnSaveActive(): boolean {
    return this.confirmOnSave && !this.editInline;
  }

  private saveConfirmTimeoutRef?: number;

  @Input()
  showIconWhenValueEmpty: boolean = false;

  editingDropdownOptionsContainerAttachToBody = input<boolean>(false);
  editingPopupAttachToBody = input<boolean>(false);

  flex = input<boolean>(false);

  tagValue = signal<string[] | null>(null);

  //TODO: gestire anche per non inline
  selectButtons = input<any[] | undefined>(undefined);

  get validationRulesInput(): ValidationRules {
    return this.validationRules();
  }

  @Input({ alias: "validationRules" })
  set validationRulesInput(value: ValidationRules) {
    this.validationRules.set(value);
  }

  validationRules = signal<ValidationRules>([]);

  isRequired = computed<boolean>(() => {
    return some(this.validationRules(), (x) => x.type === "required");
  });

  @Input()
  showClearButton: boolean = false;

  @Input()
  showEditableClearButton: boolean = false;

  @Input()
  label: string | undefined;

  @Input()
  showLabelOnlyOnEdit: boolean = false;

  @Input()
  showLabelOnlyOnDisplay: boolean = false;

  @Input()
  hideLabelOnDisplay: boolean = false;

  @Bind
  shouldShowDisplayLabel(): boolean {
    return (
      !!this.label && !this.showLabelOnlyOnEdit && !this.hideLabelOnDisplay
    );
  }

  @Bind
  shouldShowDisplayLabelOrIcon(): boolean {
    return !!this.icon || this.shouldShowDisplayLabel();
  }

  @Input()
  editWidth: number | undefined;

  @Input()
  labelSmall: boolean | undefined;

  @Input()
  icon: string | undefined;

  @Input()
  selectSearchEnabled: boolean = true;

  @Input()
  selectMinSearchLength: number = 3;

  @Input()
  lookupTitle: string | undefined;

  @Input()
  showLabelBottomBorder: boolean = false;

  @Input()
  labelLight: boolean = false;

  @Input()
  width: number | undefined;

  @Input()
  companionSelects?: EditableCompanionSelectConfig[];

  @Input()
  dateDisplayFormat?: string;

  @Input()
  dateTimeOptions?: {
    minute?: {
      readonly?: boolean;
    };
  };

  //TODO: manage acceptCustomValue for dxi-item, default true caused bug SNT-8375
  @Input()
  acceptCustomValue: boolean = true;

  onOpened: (e: any) => void = (e) => {};

  enableDelete = input<boolean>();

  enableRecalculatePercentage = input<boolean>(false);
  recalculatePercentage: boolean = true;

  linkable = input(false);

  cursor = input<"default" | "pointer">("default");

  // default su DESKTOP
  textNoWrap = input<boolean>(inject(PLATFORM_TOKEN) === PlatformType.DESKTOP);

  @Output()
  changed: EventEmitter<EditableChangedEvent> =
    new EventEmitter<EditableChangedEvent>();

  @Output()
  deleted: EventEmitter<EditableChangedEvent> =
    new EventEmitter<EditableChangedEvent>();

  @Output()
  clear: EventEmitter<EditableChangedEvent> =
    new EventEmitter<EditableChangedEvent>();

  @Output()
  codiceFiscaleResult: EventEmitter<CodiceFiscaleResult> =
    new EventEmitter<CodiceFiscaleResult>();

  @Output()
  linkClick: EventEmitter<void> = new EventEmitter();

  @ContentChild(EditableSelectBoxItemTemplateDirective)
  selectBoxItemTemplate: EditableSelectBoxItemTemplateDirective | undefined;

  isEditing: boolean = false;
  isUpdating: boolean = false;
  isClosing: boolean = false;

  positionDialog = input<{
    left?: number;
    top?: number;
    width?: number;
    height?: number;
  }>({
    left: undefined,
    top: undefined,
    width: undefined,
    height: undefined
  });

  inputClientRect:
    | {
        left: number;
        top: number;
        width: number;
        height: number;
      }
    | undefined;

  validationStatus: "valid" | "invalid" | "validating" = "valid";
  validationTouched: boolean = false;

  formData: (any & { value: any }) | undefined;

  lockedOpened: boolean = false;

  dxEditorType:
    | "dxTextBox"
    | "dxTextArea"
    | "dxNumberBox"
    | "dxDateBox"
    | "dxSelectBox"
    | "dxLookup"
    | undefined;

  dxWidth: number | undefined;
  dxDateBoxType: "date" | "time" | "datetime" | undefined;
  dxDateBoxInterval: number | undefined;

  private readonly id = nextRuntimeId();
  private readonly fiscalCodeService = inject(CodiceFiscaleService);
  private readonly customerBakend = inject(CustomerBackend);
  private readonly bootstrapService = inject(BootstrapService);
  private readonly addressCheckerFunctionService = inject(
    AddressCheckerFunctionService
  );

  readonly elementRef: ElementRef = inject(ElementRef);
  private readonly injector = inject(Injector);

  validationService = inject(ValidationService);

  errors: string[] = [];

  readonly VAT_NUMBER_CODE_AND_COUNTRIES = VAT_NUMBER_CODE_AND_COUNTRIES;

  dirty: boolean = false;

  prefixPhoneDataSource!: DataSource;

  /** Must sit above the popup overlay (`z-[9999]` in the template). */
  private static readonly EDITING_DROPDOWN_Z_INDEX = 10_000;

  /** Use for dialogs opened while an `snt-editable` editor is active. */
  static readonly ABOVE_OVERLAY_Z_INDEX = 10_001;

  /** DevExtreme dropdowns inside those dialogs (e.g. dxSelectBox). */
  static readonly POPUP_DROPDOWN_Z_INDEX = 10_002;

  readonly companionSelectDropDownOptions: {
    container?: HTMLElement;
    zIndex?: number;
  } = {};

  readonly editingDropDownOptions: {
    container?: HTMLElement;
    zIndex?: number;
    title?: string;
  } = {};

  /** Passed to `snt-editable-address` select/autocomplete editors. */
  readonly addressDropDownOptions = this.editingDropDownOptions;

  /** Passed to `snt-fiscal-code-helper` select/date editors. */
  readonly fiscalCodeDropDownOptions = this.editingDropDownOptions;

  private readonly companionSelectEditorOptionsByField = new Map<
    string,
    Record<string, unknown>
  >();

  constructor() {
    effect(() => {
      const validationRules = this.validationRules();
      if (validationRules && validationRules.length > 0) {
        this.validationService.addToGroup(this.validationGroup!, {
          validate: () => {
            this.validationTouched = true;

            const validationErrors = this.validationService.validateRules(
              this.value,
              validationRules
            );

            this.validationStatus =
              validationErrors.length === 0 ? "valid" : "invalid";

            return validationErrors.length === 0;
          }
        });
      }
    });
  }

  async ngOnInit(): Promise<void> {
    switch (this.dataType) {
      case "text":
      case "fiscalCode":
      case "vatCode":
      case "phoneNumber":
      case "address": {
        this.dxEditorType = "dxTextBox";
        break;
      }
      case "textarea": {
        this.dxEditorType = "dxTextArea";
        break;
      }
      case "number":
      case "percent":
      case "currency": {
        this.dxEditorType = "dxNumberBox";
        break;
      }
      case "date":
      case "time":
      case "datetime": {
        this.dxEditorType = "dxDateBox";
        break;
      }
      case "boolean":
      case "select": {
        this.dxEditorType = "dxSelectBox";
        break;
      }
      case "lookup": {
        this.dxEditorType = "dxLookup";
        break;
      }
    }

    switch (this.dataType) {
      case "datetime": {
        this.dxDateBoxType = "datetime";

        const { minute } = this.dateTimeOptions ?? {};

        if (minute) {
          const { readonly } = minute ?? {};

          if (readonly) {
            this.onOpened = (e: any) => {
              const minuteBox =
                e.component["_strategy"]["_timeView"]["_minuteBox"];
              minuteBox.option("readOnly", true);
            };
          }
        }

        break;
      }
      case "date": {
        this.dxDateBoxType = "date";
        break;
      }
      case "time": {
        this.dxDateBoxType = "time";
        this.dxDateBoxInterval = 15;
        this.dxWidth = 120;
        break;
      }
      case "phoneNumber": {
        const updatedValidationRules: ValidationRules = [
          ...(this.validationRules() || []),
          {
            type: "custom",
            validationCallback: (options) => {
              if (!options.value) {
                return true;
              }

              return validatePhone(
                {
                  phone: options.value,
                  countryCode: this.formData?.prefixPhone ?? undefined
                },
                this.bootstrapService
              );
            }
          }
        ];
        this.validationRules.set(updatedValidationRules);
        break;
      }
      case "fiscalCode": {
        const updatedValidationRules: ValidationRules = [
          ...(this.validationRules() || []),
          {
            type: "custom",
            validationCallback: (options) => {
              if (this.fiscalCodeOptional && !options.value) {
                return true;
              }

              return this.fiscalCodeService.isValid(options.value);
            }
          }
        ];

        if (this.fiscalCodeCheckUniqueness) {
          updatedValidationRules.push({
            type: "async",
            validationCallback: (options) =>
              this.validateUniqSSN({ value: options.value }),
            message: sntTranslate(
              marker("Esiste già un cliente con questo codice fiscale")
            )
          });
        }

        this.validationRules.set(updatedValidationRules);

        const reversed = this.fiscalCodeService.reverse(this.value);
        if (reversed && this.fiscalCodeHelperData) {
          this.fiscalCodeHelperData.dataDiNascita =
            this.fiscalCodeHelperData.dataDiNascita ||
            reversed.birthday.toISOString();
          this.fiscalCodeHelperData.codiceCatastale =
            this.fiscalCodeHelperData.codiceCatastale || reversed.birthplace.cc;
        }

        break;
      }
      case "vatCode": {
        const updatedValidationRules: ValidationRules = [
          ...(this.validationRules() || []),
          {
            type: "custom",
            validationCallback: ({ value }) => {
              if (!value) {
                return true;
              }

              if (this.vatNumberForeign) {
                return validateGlobalVatNumber(value).valid;
              }

              return validateEUVatNumber(value);
            }
          }
        ];

        if (this.vatNumberCheckUniqueness) {
          updatedValidationRules.push({
            type: "async",
            validationCallback: (options) =>
              this.validateUniqVatNumber({ value: options.value }),
            message: sntTranslate(marker("Partita IVA già presente"))
          });
        }

        this.validationRules.set(updatedValidationRules);

        break;
      }
      case "boolean": {
        this.value = !!this.value;

        if (!this.dataSource) {
          this.dataSource = [
            {
              value: false,
              display: sntTranslate(marker("No"))
            },
            {
              value: true,
              display: sntTranslate(marker("Si"))
            }
          ];
        }

        this.selectSearchEnabled = false;

        break;
      }
    }

    this.prefixPhoneDataSource = new DataSource({
      store: new ArrayStore({
        data: getPrefixNumber()
      })
    });

    this.resetFormData();

    if (this.editingWidth) {
      this.dxWidth = this.editingWidth;
    }

    this.syncEditingDropDownOptions();

    await this.valueToValueDisplay();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    const companionSelectsChange = changes["companionSelects"];
    if (companionSelectsChange) {
      this.companionSelectEditorOptionsByField.clear();
    }

    const valueChange = changes["value"];

    if (valueChange && !valueChange.isFirstChange()) {
      await this.valueToValueDisplay();
    }

    const dataSourceChange = changes["dataSource"];
    if (dataSourceChange) {
      await this.valueToValueDisplay();
    }

    if (valueChange && this.dataType === "tag" && !!this.tagValueExpr) {
      this.tagValue.set(
        valueChange.currentValue.map(
          (item: Record<string, any>) => item[this.tagValueExpr!]
        )
      );
    }

    if (this.formData && valueChange) {
      this.formData.value = valueChange.currentValue;

      if (this.dataType === "phoneNumber") {
        this.formData.value = getPhoneNumber(this.value, this.bootstrapService);
        this.formData.prefixPhone =
          getRegionCodePhoneNumber(
            this.value,
            this.bootstrapService
          )?.toLowerCase() ||
          getDefaultPrefixPhone(this.bootstrapService)?.toLowerCase() ||
          "";
      }
    }
  }

  onClickDisplayContainer(clickEvent: MouseEvent): void {
    clickEvent.stopImmediatePropagation();
    clickEvent.preventDefault();
  }

  onClickEditContainer(clickEvent: MouseEvent): void {
    clickEvent.stopImmediatePropagation();
    clickEvent.preventDefault();
  }

  onFiscalCodeHelperResult(result: CodiceFiscaleResult): void {
    this.dxForm?.instance.updateData({ value: result.codiceFiscale });
  }

  onSelectPriceSuggestion(price: number): void {
    this.formData!.value = price;
    this.value = price;
    this.close();
  }

  async open(
    containerElement: HTMLElement,
    tooltipDirective: TooltipDirective | undefined
  ): Promise<void> {
    tooltipDirective?.hideTooltip();

    this.dirty = false;

    const clientRect = containerElement.getBoundingClientRect();

    const positionDialog = this.positionDialog();

    this.inputClientRect = {
      left: positionDialog.left ?? clientRect.left,
      top: positionDialog.top ?? clientRect.top,
      height: positionDialog.height ?? clientRect.height,
      width: positionDialog.width ?? clientRect.width
    };

    this.editPopupReference = this.buildEditPopupReference(
      containerElement,
      this.inputClientRect
    );

    this.resetFormData();
    this.syncEditingDropDownOptions();
    this.isEditing = true;

    afterNextRender(
      () => {
        if (!this.editInline && this.editingPopupAttachToBody()) {
          this.attachEditingPopupToBody();
        }

        this.setupEditPopupPositioning();
      },
      { injector: this.injector }
    );
  }

  private buildEditPopupReference(
    containerElement: HTMLElement,
    inputClientRect: {
      left: number;
      top: number;
      width: number;
      height: number;
    }
  ): HTMLElement | VirtualElement {
    const positionDialog = this.positionDialog();
    const hasPositionDialogOverride =
      positionDialog.left !== undefined ||
      positionDialog.top !== undefined ||
      positionDialog.width !== undefined ||
      positionDialog.height !== undefined;

    if (!hasPositionDialogOverride) {
      return containerElement;
    }

    return {
      getBoundingClientRect: () => {
        const { left, top, width, height } = inputClientRect;

        return {
          x: left,
          y: top,
          left,
          top,
          width,
          height,
          right: left + width,
          bottom: top + height
        } as DOMRect;
      }
    };
  }

  private setupEditPopupPositioning(): void {
    this.editPopupAutoUpdateCleanup?.();
    this.editPopupAutoUpdateCleanup = undefined;

    const reference = this.editPopupReference;
    const floating = this.editPopupPanel?.nativeElement;

    if (!reference || !floating) {
      return;
    }

    const update = (): void => {
      this.updateEditPopupPosition(reference, floating);
    };

    update();
    this.editPopupAutoUpdateCleanup = autoUpdate(reference, floating, update);
  }

  private updateEditPopupPosition(
    reference: HTMLElement | VirtualElement,
    floating: HTMLElement
  ): void {
    computePosition(reference, floating, {
      middleware: [
        offset(8),
        autoPlacement({
          alignment: "start",
          allowedPlacements: [
            "bottom-start",
            "top-start",
            "bottom-end",
            "top-end",
            "right-start",
            "left-start"
          ]
        }),
        shift({ padding: 8 })
      ]
    }).then(({ x, y }) => {
      Object.assign(floating.style, {
        left: `${x}px`,
        top: `${y}px`
      });
    });
  }

  private clearEditPopupPositioning(): void {
    this.editPopupAutoUpdateCleanup?.();
    this.editPopupAutoUpdateCleanup = undefined;
    this.editPopupReference = undefined;
  }

  private setIsEditing(value: boolean): void {
    if (!value) {
      this.clearEditPopupPositioning();
    }

    this.isEditing = value;
  }

  private attachEditingPopupToBody(): void {
    const popup = this.editPopupOverlay?.nativeElement;
    if (!popup || popup.parentElement === document.body) {
      return;
    }

    document.body.appendChild(popup);
  }

  private syncEditingDropDownOptions(): void {
    const popupEditing = !this.editInline;
    const attachDropdownToBody =
      popupEditing ||
      this.editingDropdownOptionsContainerAttachToBody() ||
      this.editingPopupAttachToBody();
    const dropdownZIndex = attachDropdownToBody
      ? EditableComponent.EDITING_DROPDOWN_Z_INDEX
      : undefined;

    this.companionSelectDropDownOptions.container = attachDropdownToBody
      ? undefined
      : this.elementRef.nativeElement;
    this.companionSelectDropDownOptions.zIndex = dropdownZIndex;

    this.editingDropDownOptions.title = this.lookupTitle;
    this.editingDropDownOptions.container = attachDropdownToBody
      ? undefined
      : this.elementRef.nativeElement;
    this.editingDropDownOptions.zIndex = dropdownZIndex;
  }

  @Bind
  getCompanionSelectEditorOptions(
    companionSelect: EditableCompanionSelectConfig
  ): Record<string, unknown> {
    let editorOptions = this.companionSelectEditorOptionsByField.get(
      companionSelect.dataField
    );

    if (!editorOptions) {
      editorOptions = {
        dataSource: companionSelect.dataSource,
        valueExpr: companionSelect.valueExpr ?? "value",
        displayExpr: companionSelect.displayExpr ?? "display",
        searchEnabled: companionSelect.searchEnabled ?? true,
        dropDownOptions: this.companionSelectDropDownOptions
      };
      this.companionSelectEditorOptionsByField.set(
        companionSelect.dataField,
        editorOptions
      );
    }

    editorOptions["dataSource"] = companionSelect.dataSource;

    return editorOptions;
  }

  async delete(): Promise<void> {
    this.deleted.emit({
      lock: () => {
        this.isUpdating = true;
      },
      unlock: () => {
        this.isUpdating = false;
      },
      updated: () => {
        this.isClosing = false;
        this.setIsEditing(false);
      },
      executeAsync: async (fnAsync: Lambda<Promise<void>>) => {
        try {
          this.isUpdating = true;

          await fnAsync();

          this.isClosing = false;
          this.setIsEditing(false);
        } catch (ex) {
          //this.formData!.value = this.value;
        } finally {
          this.isClosing = false;
          this.isUpdating = false;
          this.recalculatePercentage = true;
        }
      },
      data: this.data,
      value: this.value,
      oldValue: this.value,
      recalculatePercentages: this.recalculatePercentage
    });
  }

  onClear(): void {
    this.clear.emit({
      lock: () => {
        this.isUpdating = true;
      },
      unlock: () => {
        this.isUpdating = false;
      },
      updated: () => {
        this.isClosing = false;
        this.setIsEditing(false);
      },
      executeAsync: async (fnAsync: Lambda<Promise<void>>) => {
        try {
          this.isUpdating = true;

          await fnAsync();

          this.isClosing = false;
          this.setIsEditing(false);
        } catch (ex) {
          this.formData!.value = this.value;

          this.setIsEditing(false);
        } finally {
          this.isClosing = false;
          this.isUpdating = false;
        }
      },
      data: this.data,
      value: null,
      oldValue: null
    });
  }

  async canRelease(options?: { forceValidate?: boolean }): Promise<boolean> {
    if (this.dxForm && !this.dirty && !options?.forceValidate) {
      return true;
    }

    if (this.dxForm) {
      let validation = this.dxForm.instance.validate();

      if (validation.complete) {
        validation = await validation.complete;
      }

      if (!validation.isValid) {
        this.validationTouched = true;
        this.validationStatus = "invalid";
        return false;
      }

      return true;
    } else {
      return true;
    }
  }

  async cancel(): Promise<void> {
    this.validationTouched = false;
    this.validationStatus = "valid";

    this.dirty = false;
    this.resetFormData();

    this.setIsEditing(false);
    this.resetSaveConfirmPending();
    this.resetSaveConfirmMessage();
  }

  ngOnDestroy(): void {
    this.clearSaveConfirmTimeout();
    this.clearEditPopupPositioning();
  }

  private startSaveConfirmTimeout(): void {
    this.clearSaveConfirmTimeout();

    const timeoutMs = Math.max(this.confirmOnSaveTimeoutMs, 0);
    if (timeoutMs === 0) {
      return;
    }

    this.saveConfirmTimeoutRef = window.setTimeout(() => {
      this.resetSaveConfirmPending();
    }, timeoutMs);
  }

  private clearSaveConfirmTimeout(): void {
    if (this.saveConfirmTimeoutRef !== undefined) {
      window.clearTimeout(this.saveConfirmTimeoutRef);
      this.saveConfirmTimeoutRef = undefined;
    }
  }

  private resetSaveConfirmPending(): void {
    this.clearSaveConfirmTimeout();
    this.saveConfirmPending = false;
  }

  private resetSaveConfirmMessage(): void {
    this.saveConfirmMessagePending = false;
    this.saveConfirmMessageConfig = null;
  }

  private async resolveSaveConfirmConfig(
    ctx: EditableSaveConfirmContext
  ): Promise<EditableSaveConfirmConfig | null> {
    if (this.dataType === "fiscalCode" && this.fiscalCodeCheckConsistencyFn) {
      const isConsistency = this.fiscalCodeCheckConsistencyFn(
        ctx.value as string
      );

      if (!isConsistency) {
        return {
          title: sntTranslate(marker("Codice fiscale non coerente")),
          text: sntTranslate(
            marker(
              "Il codice fiscale è valido ma non coerente con i dati anagrafici inseriti, vuoi proseguire?"
            )
          ),
          cancelBlocksSave: true
        };
      }
    }

    if (this.saveConfirmFn) {
      return await this.saveConfirmFn(ctx);
    }

    return null;
  }

  getEditRecapLines(): EditableEditRecapLine[] {
    if (!this.editRecapBuilder) {
      return [];
    }

    return this.editRecapBuilder({
      data: this.data,
      formData: this.formData
    });
  }

  @Bind
  async onSave(
    buttonClickAsyncEvent?: ButtonClickAsyncEvent | ItemClickEvent
  ): Promise<void> {
    const asyncButton =
      buttonClickAsyncEvent && !!(<any>buttonClickAsyncEvent).execute
        ? <ButtonClickAsyncEvent>buttonClickAsyncEvent
        : undefined;

    if (this.confirmOnSaveActive && !this.saveConfirmPending) {
      const requestConfirm = async () => {
        if (!(await this.canRelease({ forceValidate: true }))) {
          return;
        }

        this.saveConfirmPending = true;
        this.startSaveConfirmTimeout();
      };

      if (asyncButton) {
        await asyncButton.execute(requestConfirm);
      } else {
        await requestConfirm();
      }

      return;
    }

    if (this.saveConfirmMessagePending) {
      return;
    }

    const executeSave = async () => {
      if (!(await this.canRelease({ forceValidate: true }))) {
        return;
      }

      const config = await this.resolveSaveConfirmConfig({
        value: this.formData?.value,
        oldValue: this.value,
        data: this.data,
        formData: this.formData
      });

      if (config) {
        this.saveConfirmMessageConfig = config;
        this.saveConfirmMessagePending = true;

        return;
      }

      this.pendingSaveConfirmAccepted = undefined;

      try {
        await this.close();
      } finally {
        this.resetSaveConfirmPending();
        this.pendingSaveConfirmAccepted = undefined;
      }
    };

    if (asyncButton) {
      await asyncButton.execute(executeSave);
    } else {
      await executeSave();
    }
  }

  @Bind
  async onSaveConfirmYes(
    buttonClickAsyncEvent?: ButtonClickAsyncEvent
  ): Promise<void> {
    const asyncButton =
      buttonClickAsyncEvent && !!(<any>buttonClickAsyncEvent).execute
        ? <ButtonClickAsyncEvent>buttonClickAsyncEvent
        : undefined;

    const executeConfirmYes = async () => {
      this.pendingSaveConfirmAccepted = true;
      this.resetSaveConfirmMessage();

      try {
        await this.close();
      } finally {
        this.resetSaveConfirmPending();
        this.pendingSaveConfirmAccepted = undefined;
      }
    };

    if (asyncButton) {
      await asyncButton.execute(executeConfirmYes);
    } else {
      await executeConfirmYes();
    }
  }

  @Bind
  onInlineEditorInitialized(dxEvent: InitializedEvent): void {
    if (this.companionSelects?.length) {
      return;
    }

    // FIXME il setTimeout è necessario per ritardare l'invocazione del metodo
    // focus() dopo l'inizializzazione del componente
    setTimeout(() => {
      dxEvent.component?.focus();
    }, 0);
  }

  @Bind
  async onFocusOutSaveOrCancel(): Promise<void> {
    if (this.companionSelects?.length) {
      return;
    }

    switch (true) {
      case this.dirty: {
        await this.onSave();
        break;
      }
      default: {
        await this.cancel();
        break;
      }
    }
  }

  @Bind
  async onCancel(
    buttonClickAsyncEvent?: ButtonClickAsyncEvent | any
  ): Promise<void> {
    const executeCancel = async () => {
      await this.cancel();
    };

    if (buttonClickAsyncEvent && !!(<any>buttonClickAsyncEvent).execute) {
      await buttonClickAsyncEvent.execute(executeCancel);
    } else {
      await executeCancel();
    }
  }

  onFieldDataChanged(): void {
    this.dirty = true;
    this.resetSaveConfirmPending();
    this.resetSaveConfirmMessage();
  }

  async close(): Promise<void> {
    if (!(await this.canRelease())) {
      return;
    }

    this.validationTouched = false;
    this.validationStatus = "valid";

    let value = this.formData?.value;
    const oldValue = this.value;
    const editBaseline = this.getEditBaselineValue();

    if (!this.hasFormDataChanges(value, editBaseline)) {
      this.setIsEditing(false);
      this.resetSaveConfirmPending();
      this.resetSaveConfirmMessage();
      return;
    }

    if (this.dataType === "phoneNumber") {
      value = hasText(value)
        ? buildPhoneNumberWithPrefix(
            { phone: value, countryCode: this.formData?.prefixPhone },
            this.bootstrapService
          )
        : "";
    }

    if (this.dataType === "address" && !this.skipAddressChecker) {
      const confirmedAddress = await this.addressCheckerFunctionService.execute(
        {
          originalAddress: this.value,
          updatedAddress: this.formData.value
        },
        !this.editInline
          ? {
              popupAttachToBody: true,
              zIndex: EditableComponent.EDITING_DROPDOWN_Z_INDEX
            }
          : undefined
      );

      if (confirmedAddress) {
        value = confirmedAddress;
        this.formData.value = confirmedAddress;
      } else {
        this.setIsEditing(false);
        this.formData.value = oldValue;
        return;
      }
    }

    const saveConfirmAccepted = this.pendingSaveConfirmAccepted;

    this.isClosing = true;

    await new Promise<void>((resolve, reject) => {
      let settled = false;
      let executeAsyncInvoked = false;

      const settleResolve = (): void => {
        if (settled) {
          return;
        }

        settled = true;
        resolve();
      };

      const settleReject = (error: unknown): void => {
        if (settled) {
          return;
        }

        settled = true;
        reject(error);
      };

      this.changed.emit({
        lock: () => {
          this.isUpdating = true;
        },
        unlock: () => {
          this.isUpdating = false;
        },
        updated: () => {
          this.isClosing = false;
          this.setIsEditing(false);

          if (!executeAsyncInvoked) {
            settleResolve();
          }
        },
        executeAsync: async (fnAsync: Lambda<Promise<void>>) => {
          executeAsyncInvoked = true;

          try {
            this.isUpdating = true;

            await fnAsync();

            this.isClosing = false;
            this.setIsEditing(false);
            settleResolve();
          } catch (ex) {
            this.isClosing = false;
            settleReject(ex);
            throw ex;
          } finally {
            this.isClosing = false;
            this.isUpdating = false;
            this.recalculatePercentage = true;
          }
        },
        data: this.data,
        value: value,
        oldValue: this.value,
        recalculatePercentages: this.recalculatePercentage,
        companionValues: this.getCompanionValues(),
        saveConfirmAccepted
      });

      queueMicrotask(() => {
        if (settled || executeAsyncInvoked || this.isUpdating) {
          return;
        }

        this.isClosing = false;
        settleResolve();
      });
    });
  }

  mainValueColSpan(): number {
    if (this.companionSelects?.length) {
      return 3;
    }

    return this.dataType === "phoneNumber" ? 2 : 3;
  }

  private getEditBaselineValue(): unknown {
    if (this.editInitialValue !== undefined) {
      return this.editInitialValue;
    }

    return this.value;
  }

  private getFormEditValue(): unknown {
    if (this.editInitialValue !== undefined) {
      return this.editInitialValue;
    }

    if (this.dataType === "phoneNumber") {
      return getPhoneNumber(this.value, this.bootstrapService);
    }

    return this.value;
  }

  private resetFormData(): void {
    this.resetSaveConfirmPending();
    this.formData = { value: this.getFormEditValue() };

    if (this.dataType === "phoneNumber") {
      this.formData.prefixPhone =
        getRegionCodePhoneNumber(
          this.value,
          this.bootstrapService
        )?.toLowerCase() ||
        getDefaultPrefixPhone(this.bootstrapService)?.toLowerCase() ||
        "";
    }

    this.companionSelects?.forEach((companionSelect) => {
      this.formData![companionSelect.dataField] = companionSelect.initialValue;
    });
  }

  private getCompanionValues(): Record<string, unknown> | undefined {
    if (!this.companionSelects?.length || !this.formData) {
      return undefined;
    }

    return this.companionSelects.reduce<Record<string, unknown>>(
      (companionValues, companionSelect) => {
        companionValues[companionSelect.dataField] =
          this.formData![companionSelect.dataField];

        return companionValues;
      },
      {}
    );
  }

  private hasFormDataChanges(value: unknown, editBaseline: unknown): boolean {
    if (value !== editBaseline) {
      return true;
    }

    if (!this.companionSelects?.length) {
      return false;
    }

    return this.companionSelects.some((companionSelect) => {
      return (
        this.formData?.[companionSelect.dataField] !==
        companionSelect.initialValue
      );
    });
  }

  private async valueToValueDisplay(): Promise<void> {
    switch (this.dataType) {
      case "address": {
        this.valueDisplay = formatAddress(this.formData.value);
        break;
      }
      case "lookup":
      case "select": {
        switch (true) {
          case !this.isEditing &&
            !!this.displayModeDisplayExprFn &&
            !!this.displayModeDisplayExprData: {
            this.valueDisplay = this.displayModeDisplayExprFn(
              this.displayModeDisplayExprData
            );
            break;
          }
          case isArray(this.dataSource): {
            this.valueDisplay = getValueDisplayFromArrayDataSource(
              <Array<{ value: string; display: string }>>this.dataSource,
              this.value
            );
            break;
          }
          default: {
            this.valueDisplay = await getValueDisplayFromDxDataSource(
              <DataSource>this.dataSource,
              this.value,
              this.displayOnlyExpr || this.displayExpr
            );
          }
        }
        break;
      }
      case "phoneNumber": {
        this.valueDisplay = getPhoneNumber(this.value, this.bootstrapService);
        break;
      }
      case "tag": {
        this.valueDisplay = undefined;
        break;
      }
      default: {
        if (this.displayExpr) {
          if (isString(this.displayExpr)) {
            this.valueDisplay = this.value[this.displayExpr];
          }

          if (isFunction(this.displayExpr)) {
            this.valueDisplay = this.displayExpr(this.value);
          }
        } else {
          this.valueDisplay = this.value;
        }
        break;
      }
    }
  }

  @Bind
  onEditorContentReady({
    component,
    element
  }: {
    component: dxTextBox;
    element: HTMLElement;
  }): void {
    switch (this.dxEditorType) {
      case "dxTextBox":
      case "dxNumberBox": {
        component.focus();
        (<HTMLInputElement>(
          element.querySelector("input[editableInput=true]")
        ))?.select();
        break;
      }
    }
  }

  @Bind
  selectBoxItemTemplateFn(
    data: any,
    index: number,
    element: HTMLElement
  ): void {
    const embeddedView =
      this.selectBoxItemTemplate?.viewContainer.createEmbeddedView(
        this.selectBoxItemTemplate?.templateRef,
        {
          data
        }
      );

    embeddedView?.rootNodes.forEach((node) => element.appendChild(node));
  }

  onWysiwygEditorContentChanged(
    wysiwygEditorComponent: WysiwygEditorComponent
  ): void {
    this.formData.value = wysiwygEditorComponent.getContent(true);
  }

  addressAdressItemTeplate(
    data: IndirizzoServiceSearchResult,
    index: number,
    element: HTMLElement
  ): void {
    const span = document.createElement("span");
    span.classList.add("p-2");
    span.innerText = data.formattedAddress;

    element.appendChild(span);
  }

  @Bind
  onAddressAddressSelectionChanged(address: Address): void {
    this.formData.value = address;
  }

  @Bind
  onTagValueChanged(dxEvent: ValueChangedEvent): void {
    if (!this.formData) {
      return;
    }

    this.formData.value = dxEvent.value ?? [];
    this.dirty = true;
  }

  @Bind
  tagTemplate(tag: TagLight | any, tagElement: HTMLElement) {
    const DEFAULT_COLOR = COLORS.CARCOAL_GRAY;
    const borderColor = tag.color || DEFAULT_COLOR;
    const textColor = colorGetLighter(tag.color || DEFAULT_COLOR, 85);
    const backgroundColor = tag.color || DEFAULT_COLOR;
    const displayValue: string =
      (Object.entries(tag ?? {})
        .filter(([key, value]) => key === this.displayExpr)
        .map(([key, value]) => value)
        .at(0) as string) ?? "";

    return el(
      "div",
      {
        class: trim(`dx-tag-content text-xs`),
        style: {
          "background-color": backgroundColor,
          "border-width": "1px",
          "border-color": borderColor,
          "padding-left": "2px",
          "padding-right": "22px",
          "padding-top": "0",
          "padding-bottom": "0",
          "margin-top": "7px"
        }
      },
      compact([
        el(
          "span",
          {
            class: "text-xs font-bold",
            style: `color: ${textColor} !important;`
          },
          displayValue
        ),
        el(
          "div",
          {
            class: "dx-tag-remove-button pms-tag-box"
          },
          [
            el("i", {
              class: "fa-solid fa-xmark",
              style: {
                color: textColor,
                "margin-top": "1px",
                "margin-right": "3px"
              }
            })
          ]
        )
      ])
    );
  }

  @Bind
  async validateUniqSSN({ value }: { value: string }): Promise<void> {
    if (!value) {
      return;
    }

    const custmersWithSameSSN = await lastValueFrom(
      this.customerBakend.searchBySSN(value)
    );

    if (custmersWithSameSSN.length > 0) {
      throw new SsnNotUniqError("");
    }
  }

  @Bind
  async validateUniqVatNumber({ value }: { value: string }): Promise<void> {
    if (!value) {
      return;
    }

    const custmersWithSameVatNumber = await lastValueFrom(
      this.customerBakend.searchByVatNumber(value)
    );

    if (custmersWithSameVatNumber.length > 0) {
      throw new VatNumberNotUniqError("");
    }
  }

  getIconFromNumber(value: string): string {
    return (
      getRegionCodePhoneNumber(value, this.bootstrapService) ?? ""
    ).toLowerCase();
  }

  cssClassPhoneNumber(value: string): string {
    return `fi fi-${this.getIconFromNumber(value)}`;
  }

  protected readonly console = console;
}

function getValueDisplayFromArrayDataSource(
  dataSource: Array<{ value: string; display: string }>,
  value: any
): any {
  return dataSource.find((x) => x.value === value)?.display;
}

async function getValueDisplayFromDxDataSource(
  dataSource: DataSource,
  value: any,
  displayExpr?: string | ((value: any) => string)
): Promise<any> {
  const dataSourceValue = await dataSource.store().byKey(value);

  if (!dataSourceValue) {
    return "";
  }

  if (!!displayExpr) {
    if (isString(displayExpr)) {
      return dataSourceValue ? dataSourceValue[displayExpr] : undefined;
    }

    if (isFunction(displayExpr)) {
      return displayExpr(dataSourceValue);
    }
  }

  return dataSourceValue;
}
